import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Clock3, ShieldCheck, HeartHandshake, Lightbulb,
} from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const STAGES = [
  { id: "stage-awareness", name: "You notice the drag", feel: "\"This is slowing us down.\"", detail: "Something on this site names a pain you already felt. That recognition is the whole first step — nothing to do with it yet." },
  { id: "stage-interest", name: "You ask, softly", feel: "\"Show me where I can simplify.\"", detail: "Thirty seconds in the form. A personal reply from Bruno — not a sequence, not a sales rep — within one business day." },
  { id: "stage-trial", name: "You see it, live", feel: "\"That's our actual answer.\"", detail: "On the activation call, your own question answered from your own record — usually inside the first ten minutes. This is the moment the trial is designed around." },
  { id: "stage-decision", name: "You hold the proof", feel: "\"The hours are real.\"", detail: "At day 30, a measured capacity report: hours returned, per seat, on your numbers. It's yours to keep whether or not we continue." },
  { id: "stage-expansion", name: "You choose what's next", feel: "\"Do the books next.\"", detail: "Expansion happens only when the report justifies it — one more process, one more quiet agent, one more piece of the week returned." },
];

const SOFTENERS = [
  { icon: ShieldCheck, text: "You can stop at any stage — there is nothing to cancel, ever." },
  { icon: HeartHandshake, text: "Every reply comes from a person. There are no automated sequences here." },
  { icon: Clock3, text: "The first live answer usually arrives inside ten minutes of the activation call." },
];

const FAQS = [
  { q: "What exactly happens after I send the form?", a: "One personal email from Bruno within one business day — a couple of questions about where your team's time goes, and an offer of a short call. If it's not a fit, you'll hear that honestly too." },
  { q: "Do I have to commit to anything to see it work?", a: "No. The 30-day trial is free, runs on your real Zoho data, and ends with a capacity report you keep either way. The decision comes after the proof, never before." },
  { q: "What if we're not ready to change systems?", a: "You won't change any. Everything works inside the Zoho you already run, and your team's daily tools stay exactly where they are." },
];

const pageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "Get Started with Ocean Data",
      description: "A calm five-stage path from 'this is slowing us down' to measured capacity returned — starting with a free 30-day trial and a first live answer in under ten minutes.",
      url: "https://oceandata.ca/get-started",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function GetStartedPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Get Started — Show Me Where I Can Simplify | Ocean Data";
  }, []);

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]">
      {/* Hero — one dominant CTA, loss-aversion micro-copy */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Get started · gently
          </p>
          <h1
            data-testid="getstarted-headline"
            className="font-display font-bold tracking-tight leading-[1.04] text-brand-navy text-4xl sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.15} testId="getstarted-headline-line-1">One small question.</MaskedLine>
            <MaskedLine delay={0.3} testId="getstarted-headline-line-2">
              <span className="text-brand-teal">Then proof, at your pace.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            There's no demo to book and nothing to buy. There's a short form,
            a personal reply, and — when you're ready — your own numbers
            answering your own question, usually inside ten minutes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10"
          >
            <button
              data-testid="getstarted-primary-cta"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-lg font-semibold px-12 py-5 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5 shadow-[0_24px_48px_-16px_rgba(14,20,51,0.4)]"
            >
              Show me where I can simplify
              <ArrowRight size={20} />
            </button>
            <p className="mt-4 text-xs text-slate-400">
              30 seconds · a personal reply within one business day · nothing to cancel
            </p>
          </motion.div>
        </div>
      </section>

      {/* The five stages — the funnel, made visible and calm */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <Reveal className="mb-12 text-center">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              The whole journey, honestly mapped
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl">
              Five stages. You set the speed of every one.
            </h2>
          </Reveal>

          <div className="relative">
            <div className="absolute left-[27px] top-4 bottom-4 w-px bg-slate-200" aria-hidden="true" />
            <div className="space-y-4">
              {STAGES.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.06}>
                  <div
                    data-testid={s.id}
                    className={`relative flex gap-5 sm:gap-6 rounded-2xl border p-6 sm:p-7 transition-colors duration-300 ${
                      i === 2 ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-slate-200 hover:border-brand-teal"
                    }`}
                  >
                    <span className={`relative z-10 w-14 h-14 rounded-full flex items-center justify-center font-display font-bold text-lg shrink-0 ${
                      i === 2 ? "bg-brand-teal text-white" : "bg-brand-tealsoft text-brand-teal"
                    }`}>
                      {i + 1}
                    </span>
                    <div>
                      <p className={`font-mono text-[10px] uppercase tracking-[0.16em] font-semibold mb-1.5 ${i === 2 ? "text-brand-gold" : "text-brand-teal"}`}>
                        {s.name} {i === 2 && "· the moment it clicks"}
                      </p>
                      <p className={`font-display font-semibold text-lg sm:text-xl leading-snug mb-2 ${i === 2 ? "text-white" : "text-brand-navy"}`}>
                        {s.feel}
                      </p>
                      <p className={`text-sm leading-relaxed ${i === 2 ? "text-slate-300" : "text-slate-600"}`}>
                        {s.detail}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Loss-aversion softeners */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <Reveal>
            <div className="rounded-3xl bg-white border border-slate-200 px-7 sm:px-10 py-9 grid grid-cols-1 sm:grid-cols-3 gap-7">
              {SOFTENERS.map((s, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <span className="w-10 h-10 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center shrink-0">
                    <s.icon size={17} />
                  </span>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Made to Stick: the conversion moments */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div data-testid="getstarted-stick-panel" className="rounded-3xl bg-brand-tealsoft border border-brand-tealborder p-8 sm:p-10">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-teal font-semibold mb-6">
                <Lightbulb size={14} /> Why each step invites instead of pushes
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  ["Sticky copy", "There's no demo to book. There's a question to ask — yours."],
                  ["Visual idea", "Five stepping stones across calm water; the visitor chooses each step, none of them wobble."],
                  ["15-second film", "A form fills itself in three fields, a reply lands with a person's name on it, and ten minutes into a call a screen answers the owner's own question."],
                  ["Why it sticks", "Every stage trades a small, reversible action for a concrete reward — autonomy preserved, risk removed, proof before payment."],
                ].map(([label, value], i) => (
                  <div key={i} className="rounded-xl bg-white/70 border border-brand-tealborder/60 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-2">{label}</p>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ + final CTA */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <Reveal className="mb-8">
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl">
              The last three hesitations, answered.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible data-testid="getstarted-faq" className="w-full mb-12">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`gs-${i}`} className="border-b border-slate-200">
                  <AccordionTrigger data-testid={`getstarted-faq-q-${i}`} className="text-left font-display font-medium text-base sm:text-lg text-brand-navy hover:text-brand-teal py-6 transition-colors">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-slate-600 leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-brand-navy text-white px-7 sm:px-10 py-10 text-center">
              <h3 className="font-display font-semibold text-2xl sm:text-3xl leading-snug mb-3">
                Whenever you're ready, the water's calm.
              </h3>
              <p className="text-sm sm:text-base text-slate-300 max-w-md mx-auto mb-8">
                Or keep exploring first —{" "}
                <Link to="/demos" className="font-semibold text-brand-teal hover:text-white transition-colors">the live demos</Link>,{" "}
                <Link to="/how-it-works" className="font-semibold text-brand-teal hover:text-white transition-colors">the 30-day map</Link>, and{" "}
                <Link to="/trust" className="font-semibold text-brand-teal hover:text-white transition-colors">the guardrails</Link>{" "}
                aren't going anywhere.
              </p>
              <button
                data-testid="getstarted-bottom-cta"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-10 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
              >
                Show me where I can simplify
                <ArrowRight size={18} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
    </main>
  );
}
